/** @jsxImportSource theme-ui */

import React from 'react';
import { Button, ButtonProps } from '../Button';
import {
    useForm,
    useFormContext,
    UseFormValues,
    UseFormInstance,
    ValidateResult,
    useField,
    UseFieldInstanceMeta,
    UseFormInstanceMeta,
    UseFieldInstancePropsType,
    UseFieldInstance,
    UseFieldOptions,
} from 'react-form';
import { FormInputWrapperProps } from '../utils/InputWrapper';
import { Switch, Textarea, Input, InputProps, Select, SelectProps, TextareaProps } from '../..';

type ValidateFunction<T> = (
    values: UseFormValues<T>,
    instance: UseFormInstance<T, unknown, unknown, any, any>
) => void | ValidateResult | Promise<ValidateResult>;

type UseFieldOptionsShimProps<T> = UseFieldOptions<
    T,
    string,
    unknown,
    UseFieldInstanceMeta<string>,
    UseFormInstanceMeta<string>,
    UseFieldInstancePropsType<T>
>;

export type UseFieldWithConnectedDefaultReturn<T> = UseFieldInstance<
    T,
    string,
    unknown,
    UseFieldInstanceMeta<string>,
    UseFormInstanceMeta<string>,
    UseFieldInstancePropsType<T>
>;
export interface FormInputProps<T> extends FormInputWrapperProps {
    name: string;
    defaultValue?: UseFieldOptionsShimProps<T>['defaultValue'];
    validate?: UseFieldOptionsShimProps<T>['validate'];
    /* In the future, we should see if we can move the idea of default isTouched to the form level */
    defaultIsTouched?: boolean;
}

interface FormTextInputProps<T> extends FormInputProps<T>, Pick<InputProps, 'maxLength' | 'placeholder'> {}
interface FormTextareaProps
    extends FormTextInputProps<string>,
        Omit<TextareaProps, 'onChange' | 'value' | 'name' | 'defaultValue' | 'variant'> {}
export interface FormProps<T> {
    onSubmit: (
        values: UseFormValues<T>,
        instance: UseFormInstance<T, unknown, unknown, any, any>
    ) => Promise<void> | void;
    validate?: ValidateFunction<T>;
    defaultValues?: UseFormValues<T>;
}

export function useFieldWithConnectedDefault<T>(
    name: string,
    { defaultValue, validate, defaultIsTouched }: UseFieldOptionsShimProps<T>,
    fallbackDefault: unknown = ''
): UseFieldWithConnectedDefaultReturn<T> {
    const formFields = useField<T>(name, { defaultValue, validate, defaultIsTouched });
    React.useEffect(() => {
        const valueToSet = (defaultValue || formFields?.form?.values[name]) ?? fallbackDefault;
        if (!formFields?.meta?.isTouched && valueToSet !== formFields?.value) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            formFields?.setValue(() => valueToSet, { isTouched: false });
        }
    }, [defaultValue, name, formFields, fallbackDefault]);
    return formFields;
}

export function FormInput({
    name,
    defaultValue,
    validate,
    defaultIsTouched,
    ...otherProps
}: FormTextInputProps<string | number>): JSX.Element {
    const { value, meta, form, getInputProps } = useFieldWithConnectedDefault<string | number>(name, {
        defaultValue,
        validate,
        defaultIsTouched,
    });

    return (
        <Input
            value={value}
            errorMessage={meta.error}
            isError={Boolean(meta.error)}
            disabled={form?.meta?.isSubmitting}
            {...getInputProps()}
            {...otherProps}
        />
    );
}

export function FormSwitch({
    name,
    defaultValue,
    validate,
    defaultIsTouched,
    ...otherProps
}: FormTextInputProps<boolean>): JSX.Element {
    const { getInputProps, value, form } = useFieldWithConnectedDefault<boolean>(
        name,
        {
            defaultValue,
            validate,
            defaultIsTouched,
        },
        false
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onChange, value: _, ...otherInputProps } = getInputProps();

    return (
        <Switch
            disabled={form?.meta?.isSubmitting}
            {...otherInputProps}
            checked={value}
            onChange={(e) =>
                onChange({
                    target: { value: e.target.checked },
                } as unknown as React.ChangeEvent<Element>)
            }
            {...otherProps}
        />
    );
}

interface FormSelectProps<T>
    extends FormInputProps<T>,
        Pick<
            SelectProps,
            'options' | 'isSearchable' | 'isClearable' | 'backspaceRemovesValue' | 'optionComponent' | 'placeholder'
        > {}

export function FormSelect({
    name,
    defaultValue,
    validate,
    defaultIsTouched,
    ...otherProps
}: FormSelectProps<string | number>): JSX.Element {
    const { meta, getInputProps, form, value } = useFieldWithConnectedDefault<string | number>(name, {
        defaultValue,
        validate,
        defaultIsTouched,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onChange, value: _, ...otherInputProps } = getInputProps();

    return (
        <Select
            disabled={form?.meta?.isSubmitting}
            {...otherInputProps}
            onChange={(e) =>
                onChange({
                    target: { value: e.value, label: e.label },
                } as unknown as React.ChangeEvent<Element>)
            }
            placeholder={otherProps.placeholder}
            defaultValue={otherProps.options.find((option) => option.value === value)}
            errorMessage={meta.error}
            isError={Boolean(meta.error)}
            {...otherProps}
        />
    );
}

export function FormButton(props: ButtonProps): JSX.Element {
    const instance = useFormContext();
    return <Button variant="primary" type="submit" disabled={instance?.meta?.isSubmitting} {...props} />;
}

export function FormTextarea({
    name,
    defaultValue,
    validate,
    defaultIsTouched,
    ...otherProps
}: FormTextareaProps): JSX.Element {
    const { value, meta, form, getInputProps } = useFieldWithConnectedDefault<string>(name, {
        defaultValue,
        validate,
        defaultIsTouched,
    });

    return (
        <Textarea
            value={value}
            errorMessage={meta.error}
            isError={Boolean(meta.error)}
            disabled={form?.meta?.isSubmitting}
            {...getInputProps()}
            {...otherProps}
        />
    );
}

export function Form({
    onSubmit,
    validate,
    defaultValues,
    ...otherProps
}: React.PropsWithChildren<FormProps<unknown>>): JSX.Element {
    const { Form: ReactForm } = useForm({
        validate,
        defaultValues,
        onSubmit,
    });
    return <ReactForm sx={{ width: '100%', height: '100%' }} {...otherProps} />;
}

export { useField, useFormContext } from 'react-form';
export type { UseFormValues, UseFieldOptions } from 'react-form';
